import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search, X, Activity } from "lucide-react";

const STATUS_STYLES = {
  "Contacted":     "border-[var(--line)] text-[var(--ink-2)] bg-white",
  "Responded":     "border-[#0369A1] text-[#0369A1] bg-[#F0F9FF]",
  "Proposal Sent": "border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF]",
  "Won":           "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]",
  "Lost":          "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]",
};
const CHANNEL_STYLES = {
  "Facebook": "bg-[#E6EDFF] text-[#1E40AF]", "Bark": "bg-[#FEF3C7] text-[#B45309]",
  "Referral": "bg-[#DCFCE7] text-[#166534]", "Website": "bg-[#E0E7FF] text-[#3730A3]",
  "Other": "bg-[var(--bg-2)] text-[var(--ink-2)]",
};
const EMPTY_FILTERS = { channel: "", status: "", assigned_to_id: "" };

export default function Sales() {
  const [leads, setLeads] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [options, setOptions] = useState({ channels: [], statuses: [] });
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  async function load() {
    const params = { ...filters };
    if (q) params.q = q;
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
    const [l, d] = await Promise.all([api.get("/sales", { params }), api.get("/sales/dashboard")]);
    setLeads(l.data);
    setDashboard(d.data);
  }

  useEffect(() => {
    api.get("/sales/options").then(r => setOptions(r.data));
    api.get("/sales/users").then(r => setUsers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.channel, filters.status, filters.assigned_to_id]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  async function remove(l) {
    if (!window.confirm(`Delete lead ${l.lead_id}?`)) return;
    await api.delete(`/sales/${l.id}`);
    toast.success("Deleted");
    load();
  }

  const setF = (k) => (v) => setFilters(f => ({ ...f, [k]: v }));
  const anyFilter = q || filters.channel || filters.status || filters.assigned_to_id;

  return (
    <div className="px-5 py-4 max-w-7xl mx-auto" data-testid="sales-page">
      <div className="flex items-center gap-3 mb-3">
        <h1 className="text-lg font-serif-strong leading-none">Sales</h1>
        <div className="h-4 w-px bg-[var(--line)]" />
        {dashboard && <KpiStrip counts={dashboard.status_counts} active={filters.status} onPick={(k) => setF("status")(filters.status === k ? "" : k)} />}
        <div className="flex-1" />
        <div className="relative w-56">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input data-testid="sales-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search leads…" className="input-slim w-full !pl-6 text-xs" />
        </div>
        {anyFilter && (
          <button data-testid="sales-clear-filters" onClick={() => { setQ(""); setFilters(EMPTY_FILTERS); }} className="text-[10px] uppercase tracking-wider text-[var(--navy)] hover:opacity-70">Clear</button>
        )}
        <button data-testid="sales-new" onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-1.5 text-xs !py-1.5 !px-2.5">
          <Plus size={12} /> New lead
        </button>
      </div>

      {dashboard?.team_activity?.length > 0 && <TeamActivity rows={dashboard.team_activity} />}

      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim">
          <thead><tr>
            <th>Lead ID</th>
            <th>Date</th>
            <th>Contact Details</th>
            <th><HeaderFilter testid="filter-channel" label="Channel" value={filters.channel} onChange={setF("channel")} items={options.channels.map(c => [c, c])} /></th>
            <th>Requirement</th>
            <th><HeaderFilter testid="filter-status" label="Status" value={filters.status} onChange={setF("status")} items={options.statuses.map(s => [s, s])} /></th>
            <th><HeaderFilter testid="filter-assigned" label="Assigned To" value={filters.assigned_to_id} onChange={setF("assigned_to_id")} items={users.map(u => [u.id, u.name])} /></th>
            <th className="text-right" />
          </tr></thead>
          <tbody>
            {leads.map(l => <LeadRow key={l.id} l={l} onEdit={() => setEditing(l)} onDelete={() => remove(l)} />)}
            {leads.length === 0 && (
              <tr><td colSpan={8} className="text-center !py-8 text-[var(--ink-2)]" data-testid="sales-empty">No leads match. Click <b>New lead</b> to add one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(showNew || editing) && (
        <LeadModal
          lead={editing}
          users={users}
          options={options}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function KpiStrip({ counts, active, onPick }) {
  return (
    <div className="flex items-center gap-1" data-testid="sales-kpis">
      {Object.entries(counts).map(([k, v]) => (
        <button
          key={k}
          onClick={() => onPick(k)}
          data-testid={`kpi-${k.replace(" ", "-")}`}
          className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider transition-colors ${active === k ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--navy)]"}`}
        >
          {k}<span className={`font-mono-data text-[11px] ${active === k ? "text-white" : "text-[var(--ink)]"}`}>{v}</span>
        </button>
      ))}
    </div>
  );
}

function TeamActivity({ rows }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-3 text-[11px]" data-testid="team-activity">
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] text-[var(--ink-2)] mr-1"><Activity size={10} /> Last 24h</span>
      {rows.map(r => (
        <span
          key={r.user_id}
          data-testid={`team-activity-${r.user_id}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${r.active ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}
          title={r.active ? `${r.updates} updates` : "No activity"}
        >
          {r.user_name}<span className="font-mono-data">{r.updates}</span>
        </span>
      ))}
    </div>
  );
}

function HeaderFilter({ testid, label, value, onChange, items }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ appearance: "none", WebkitAppearance: "none", backgroundImage: "none", minHeight: 0, height: "auto", width: "auto", paddingRight: 0 }}
      className={`bg-transparent border-0 p-0 cursor-pointer uppercase tracking-[0.14em] text-[8.5px] font-medium outline-none ${value ? "text-[var(--navy)] underline underline-offset-2" : "text-[var(--ink-2)]"}`}
    >
      <option value="">{`${label} ▾`}</option>
      {items.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
    </select>
  );
}

function LeadRow({ l, onEdit, onDelete }) {
  return (
    <tr data-testid={`sales-row-${l.id}`}>
      <td className="font-mono-data text-[10px]">{l.lead_id}</td>
      <td className="font-mono-data text-[10px] whitespace-nowrap">{l.contact_date}</td>
      <td>{l.contact_details}</td>
      <td><span className={`inline-block text-[9px] uppercase tracking-wider px-1 py-px rounded-sm ${CHANNEL_STYLES[l.channel] || CHANNEL_STYLES.Other}`}>{l.channel}</span></td>
      <td className="text-[var(--ink-2)]"><div className="truncate max-w-xs" title={l.requirement}>{l.requirement}</div></td>
      <td><span className={`inline-flex text-[9px] uppercase tracking-wider px-1 py-px rounded-sm border ${STATUS_STYLES[l.status] || STATUS_STYLES.Contacted}`}>{l.status}</span></td>
      <td>{l.assigned_to_name || "—"}</td>
      <td className="text-right whitespace-nowrap">
        <button data-testid={`edit-${l.id}`} onClick={onEdit} className="p-1 text-[var(--ink-2)] hover:text-[var(--navy)]"><Pencil size={11} /></button>
        <button data-testid={`delete-${l.id}`} onClick={onDelete} className="p-1 text-[var(--ink-2)] hover:text-[var(--err)]"><Trash2 size={11} /></button>
      </td>
    </tr>
  );
}

function LeadModal({ lead, users, options, onClose, onSaved }) {
  const [form, setForm] = useState({
    contact_details: lead?.contact_details || "",
    channel: lead?.channel || "Website",
    requirement: lead?.requirement || "",
    status: lead?.status || "Contacted",
    ...(lead ? { contact_date: lead.contact_date || "", assigned_to_id: lead.assigned_to_id || "", assigned_to_name: lead.assigned_to_name || "" } : {}),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function save() {
    if (!form.contact_details.trim()) { toast.error("Contact details required"); return; }
    setSaving(true);
    try {
      if (lead) await api.patch(`/sales/${lead.id}`, form);
      else await api.post("/sales", form);
      toast.success(lead ? "Updated" : "Lead added");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="lead-modal">
      <div className="bg-white rounded-sm max-w-lg w-full">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)]">
          <div className="font-medium text-sm">{lead ? `Edit ${lead.lead_id}` : "New lead"}</div>
          <button data-testid="lead-close" onClick={onClose} className="text-[var(--ink-2)] hover:text-[var(--ink)]"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-2.5">
          <L label="Contact Details (name · email · phone)">
            <input data-testid="lead-contact-details" value={form.contact_details} onChange={set("contact_details")} placeholder="Jane Doe · jane@example.co.uk · 07000111222" className="input-slim w-full text-xs" />
          </L>
          <L label="Requirement / Service required">
            <textarea data-testid="lead-requirement" rows={2} value={form.requirement} onChange={set("requirement")} className="input-slim w-full text-xs" />
          </L>
          <div className="grid grid-cols-2 gap-2.5">
            <L label="Channel">
              <select data-testid="lead-channel" value={form.channel} onChange={set("channel")} className="input-slim w-full text-xs">
                {options.channels.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </L>
            <L label="Status">
              <select data-testid="lead-status" value={form.status} onChange={set("status")} className="input-slim w-full text-xs">
                {options.statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </L>
          </div>
          {lead && (
            <div className="grid grid-cols-2 gap-2.5">
              <L label="Contact Date">
                <input data-testid="lead-date" type="date" value={form.contact_date} onChange={set("contact_date")} className="input-slim w-full text-xs" />
              </L>
              <L label="Assigned To">
                <select
                  data-testid="lead-assignee"
                  value={form.assigned_to_id}
                  onChange={e => {
                    const u = users.find(x => x.id === e.target.value);
                    setForm(f => ({ ...f, assigned_to_id: e.target.value, assigned_to_name: u?.name || "" }));
                  }}
                  className="input-slim w-full text-xs"
                >
                  <option value="">— Unassigned —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </L>
            </div>
          )}
          {!lead && <div className="text-[10px] text-[var(--ink-2)]" data-testid="lead-auto-note">Dated today and assigned to you automatically.</div>}
        </div>
        <div className="flex justify-end gap-2 px-4 py-2.5 border-t border-[var(--line)]">
          <button data-testid="lead-cancel" onClick={onClose} className="btn-ghost text-xs !py-1.5 !px-2.5">Cancel</button>
          <button data-testid="lead-save" onClick={save} disabled={saving} className="btn-primary text-xs !py-1.5 !px-2.5">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] block mb-0.5">{label}</label>
      {children}
    </div>
  );
}
